/**
 * Booking status update hook
 * Handles status changes (Reservada/Cancelada)
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { BookingStatus } from "@/types/admin/booking";

/**
 * Hook to update booking status
 */
export const useUpdateBookingStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: BookingStatus;
    }) => {
      const { data: booking, error } = await supabase
        .from("bookings")
        .update({ status })
        .eq("id", id)
        .select(
          `
          *,
          origins (
            name
          )
        `,
        )
        .single();

      if (error) {
        console.error("Error updating booking status:", error);
        throw error;
      }

      return {
        ...booking,
        status: booking.status as BookingStatus,
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["availability"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-data"] });
      toast({
        title: "Estado actualizado",
        description: `La reservación ha sido ${variables.status.toLowerCase()}`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al actualizar el estado: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};
